import React, { Component } from 'react';
import PropTypes from 'prop-types';
import isNumber from 'lodash/isNumber';
import findIndex from 'lodash/findIndex';
import Components from 'components';
import { TabStripContextProvider } from 'contexts/TabStripContext';

const {
  array,
  number,
  string,
  object,
  oneOfType,
} = PropTypes;

// Small polyfill for IE that doesnt support forEach on NodeList
if (typeof NodeList !== 'undefined' && NodeList.prototype && !NodeList.prototype.forEach) {
  NodeList.prototype.forEach = Array.prototype.forEach;
}

const getNextSiblings = (el, filter) => {
  const siblings = [];
  while (el = el.nextSibling) { if (!filter || filter(el)) siblings.push(el); } // eslint-disable-line no-cond-assign, no-param-reassign, max-len
  return siblings;
};

const tabFilter = (el) => el.nodeName.toLowerCase() === 'div' && el.className.indexOf('k-tab') !== -1;

const getParentAtLevel = (element, levelsUp) => {
  if (levelsUp > 0) return getParentAtLevel(element.parentNode, levelsUp - 1);
  return element;
};

const checkElementsAreSiblings = (elm1, elm2) => (
  elm1 !== elm2 && [...elm1.parentNode.children].some((child) => child === elm2));

const getSiblings = (parent) => {
  let siblings = getNextSiblings(parent, tabFilter) || [];
  let isPackageTab = false;
  // We might be working with a PackageTabA widget in which case we will have only 1 sibling
  // and with k-tabs class on it
  if (siblings.length === 1 && siblings[0].className.indexOf('k-tabs') !== -1) {
    siblings = siblings[0].childNodes;
    isPackageTab = true;
  }
  return { siblings, isPackageTab };
};

if (typeof window !== 'undefined') {
  window.showTab = (widgetId, template, index, isSelected) => {
    const currentInstance = document.getElementById(`TabStrip-${widgetId}`);
    const isBackend = (el) => (
      el.parentNode && el.parentNode.parentNode && el.parentNode.parentNode.className.indexOf('rdContent') !== -1
    );
    let siblingTabs = [];
    if (!isBackend(currentInstance)) { // The backend has a completely different DOM structure
      const parent = getParentAtLevel(currentInstance, 1);
      siblingTabs = getSiblings(parent).siblings;
    } else {
      let parent = getParentAtLevel(currentInstance, 1);
      const { siblings, isPackageTab } = getSiblings(parent);
      siblingTabs = siblings;
      if (!isPackageTab) {
        const allTabsOnPage = document.querySelectorAll('.k-tab');
        parent = getParentAtLevel(currentInstance, 7);
        allTabsOnPage.forEach((tab) => {
          const parentOfTab = getParentAtLevel(tab, 7);
          if (checkElementsAreSiblings(parent, parentOfTab)) siblingTabs.push(tab);
          return true;
        });
      }
    }
    siblingTabs.forEach((tab, idx) => {
      if (index === idx) {
        tab.classList.add('k-tab-active');
        if (template === 'SelectB') tab.classList.add('k-tab-active-custom');
        if (template === 'SelectC' && isSelected) tab.scrollIntoView({ behavior: 'smooth', block: 'start' });
        if (template === 'SelectA') tab.classList.add('k-tab-active-no-border');
      } else {
        tab.classList.remove('k-tab-active');
        if (template === 'SelectB') tab.classList.remove('k-tab-active-custom');
        if (template === 'SelectA') tab.classList.remove('k-tab-active-no-border');
      }
    });
  };
}

class SelectContainer extends Component {
  constructor(props) {
    super(props);

    const { identifier = '' } = props;

    this.state = {
      selectedIndex: props.selectedIndex,
      widgetId: `${identifier ? `${identifier}-` : ''}select`,
    };

    this.handleClick = this.handleClick.bind(this);
    this.handleSelect = this.handleSelect.bind(this);
    this.getActiveTabFromUrl = this.getActiveTabFromUrl.bind(this);
    this.handleLoad = this.handleLoad.bind(this);
  }

  componentDidMount() {
    // move away from window.requestAnimationFrame() and setTimeout since the DOM
    // may not be fully loaded with the content containers. That's why we're adding the
    // listener
    if (document.readyState !== 'loading') {
      // document is already ready, just execute code here
      this.handleLoad();
    } else {
      document.addEventListener('DOMContentLoaded', this.handleLoad);
    }
  }

  componentWillUnmount() {
    document.removeEventListener('DOMContentLoaded', this.handleLoad);
  }

  getActiveTabFromUrl = (widgetId, template) => {
    let activeTab = 0;
    let isQueryStringPresent = false;
    const { tabs } = this.props;
    if ('URLSearchParams' in window) {
      const searchParams = new URLSearchParams(window.location.search);
      if (searchParams.has(widgetId)) {
        const tab = +searchParams.get(widgetId);
        // Check param value so we do not brake the widget with invalid ones
        if (isNumber(tab) && tab >= 0 && tab < tabs.length) {
          activeTab = tab;
          isQueryStringPresent = true;
        }
      }
    }
    if (activeTab === 0
      && (template === 'SelectB' || template === 'PackageTabC')
      && !isQueryStringPresent) {
      activeTab = -1;
    }
    return activeTab;
  }

  handleLoad = () => {
    if (typeof window !== 'undefined') {
      const { widgetId } = this.state;
      const { type } = this.props;
      window.showTab(widgetId, type, this.getActiveTabFromUrl(widgetId, type), false);
      // We need a second render since the active tab is get after the initial render
      this.setState({ selectedIndex: this.getActiveTabFromUrl(widgetId, type) });
    }
  }

  updateActiveTab = (widgetId, activeTab) => {
    if ('URLSearchParams' in window) {
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set(widgetId, activeTab);
      const newRelativePathQuery = `${window.location.pathname}?${searchParams.toString()}`;
      window.history.pushState(null, '', newRelativePathQuery);
    }
  }

  handleClick = (index) => {
    if (typeof window !== 'undefined') {
      const { widgetId } = this.state;
      const { type } = this.props;
      window.showTab(widgetId, type, index, true);
      this.updateActiveTab(widgetId, index);
      this.setState({ selectedIndex: index });
    }
  }

  handleSelect = (details) => {
    if (typeof window !== 'undefined') {
      const { tabs = [], type } = this.props;
      const { widgetId } = this.state;
      const index = findIndex(tabs, (obj) => obj.title === details.title);

      window.showTab(widgetId, type, index, true);
      this.updateActiveTab(widgetId, index);
      this.setState({ selectedIndex: index });
    }
  }

  render() {
    const { type } = this.props;
    const { selectedIndex } = this.state;
    const Select = Components[type];

    return (
      <TabStripContextProvider>
        <Select
          {...this.props}
          selectedIndex={selectedIndex}
          handleClick={this.handleClick}
          handleSelect={this.handleSelect}
        />
      </TabStripContextProvider>
    );
  }
}

SelectContainer.propTypes = {
  type: string.isRequired,
  tabs: oneOfType([
    array,
    object,
  ]),
  selectedIndex: number,
  identifier: string,
};

export default SelectContainer;
